import {
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
} from '../../api/authenticatedFetch';

const BASE_API = '/api/v1';

// --- Approvals Inbox ---

/**
 * @param {{ page_size?: number, offset?: number, filter_object_type?: string, filter_requested_by?: string, filter_amount_min?: number, filter_amount_max?: number, sort_by?: string, sort_dir?: string }} opts
 * @returns {Promise<{ inbox: import('./types').InboxItem[] }>}
 */
export const approvalsInbox = async (opts = {}) => {
  const params = {};
  if (opts.page_size != null) params.page_size = opts.page_size;
  if (opts.offset != null) params.offset = opts.offset;
  if (opts.filter_object_type) params.filter_object_type = opts.filter_object_type;
  if (opts.filter_requested_by) params.filter_requested_by = opts.filter_requested_by;
  if (opts.filter_amount_min != null) params.filter_amount_min = opts.filter_amount_min;
  if (opts.filter_amount_max != null) params.filter_amount_max = opts.filter_amount_max;
  if (opts.sort_by) params.sort_by = opts.sort_by;
  if (opts.sort_dir) params.sort_dir = opts.sort_dir;
  return authenticatedGet(`${BASE_API}/approvals/inbox`, params);
};

// --- Approval Requests ---

/**
 * @param {string|number} id
 * @returns {Promise<{ request: import('./types').ApprovalRequest }>}
 */
export const approvalRequestById = async (id) => {
  return authenticatedGet(`${BASE_API}/approval-requests/${id}`);
};

/**
 * @param {string|number} id
 * @param {{ comment?: string }} payload
 * @returns {Promise<{ request: import('./types').ApprovalRequest }>}
 */
export const approveRequest = async (id, payload = {}) => {
  return authenticatedPost(`${BASE_API}/approval-requests/${id}/approve`, payload);
};

/**
 * @param {string|number} id
 * @param {{ comment: string }} payload - comment required
 * @returns {Promise<{ request: import('./types').ApprovalRequest }>}
 */
export const rejectRequest = async (id, payload) => {
  return authenticatedPost(`${BASE_API}/approval-requests/${id}/reject`, payload);
};

/**
 * @param {string|number} id
 * @returns {Promise<{ request: import('./types').ApprovalRequest }>}
 */
export const cancelRequest = async (id) => {
  return authenticatedPost(`${BASE_API}/approval-requests/${id}/cancel`, {});
};

/**
 * Returns the request to the preparer for revision (non-terminal; distinct from reject).
 * @param {string|number} id
 * @param {{ comment: string }} payload - comment required
 * @returns {Promise<{ request: import('./types').ApprovalRequest }>}
 */
export const returnApprovalRequest = async (id, payload) => {
  return authenticatedPost(`${BASE_API}/approval-requests/${id}/return`, payload);
};

/**
 * @param {{ object_type: string, object_id: string, reason?: string, transition_type?: string, impact_summary?: string, budget_type?: string, amount_delta?: number }} payload
 * @returns {Promise<{ result: string } | { request: import('./types').ApprovalRequest }>}
 */
export const createApprovalRequest = async (payload) => {
  return authenticatedPost(`${BASE_API}/approval-requests`, payload);
};

// --- Delegations ---

/**
 * @param {{ role?: 'delegator'|'delegate'|'both' }} opts
 * @returns {Promise<{ delegations: import('./types').Delegation[] }>}
 */
export const listDelegations = async (opts = {}) => {
  const params = {};
  if (opts.role) params.role = opts.role;
  return authenticatedGet(`${BASE_API}/delegations`, params);
};

/**
 * @param {{ delegate_user_id: string, starts_at?: string, ends_at?: string, scope_json?: object, max_amount?: number }} payload
 * @returns {Promise<{ delegation: import('./types').Delegation }>}
 */
export const createDelegation = async (payload) => {
  return authenticatedPost(`${BASE_API}/delegations`, payload);
};

/**
 * @param {string|number} id
 * @returns {Promise<{ result: string }>}
 */
export const disableDelegation = async (id) => {
  return authenticatedPost(`${BASE_API}/delegations/${id}/disable`, {});
};

// --- Users ---

/**
 * @param {string} [query]
 * @returns {Promise<{ users: Array<{ id: number, email: string, first_name: string, last_name: string }> }>}
 */
export const searchUsers = async (query = '') => {
  const params = {};
  if (query) params.q = query;
  return authenticatedGet(`${BASE_API}/users`, params);
};

// --- Approval Policies ---

/**
 * @param {string|number} id
 * @returns {Promise<{ policy: import('./types').ApprovalPolicy }>}
 */
export const getApprovalPolicyById = async (id) => {
  return authenticatedGet(`${BASE_API}/approval-policies/${id}`);
};

/**
 * @param {{ object_type?: string }} opts
 * @returns {Promise<{ policies: import('./types').ApprovalPolicy[] }>}
 */
export const listApprovalPolicies = async (opts = {}) => {
  const params = {};
  if (opts.object_type) params.object_type = opts.object_type;
  return authenticatedGet(`${BASE_API}/approval-policies`, params);
};

/**
 * @param {{ object_type: string, name: string, is_enabled?: boolean, policy_json?: object }} payload
 * @returns {Promise<{ policy: import('./types').ApprovalPolicy }>}
 */
export const createApprovalPolicy = async (payload) => {
  return authenticatedPost(`${BASE_API}/approval-policies`, payload);
};

/**
 * @param {string|number} id
 * @param {{ name?: string, is_enabled?: boolean, policy_json?: object }} payload
 * @returns {Promise<{ policy: import('./types').ApprovalPolicy }>}
 */
export const updateApprovalPolicy = async (id, payload) => {
  return authenticatedPut(`${BASE_API}/approval-policies/${id}`, payload);
};

/**
 * @param {string|number} id
 * @returns {Promise<{ result: string }>}
 */
export const disableApprovalPolicy = async (id) => {
  return authenticatedPost(`${BASE_API}/approval-policies/${id}/disable`, {});
};
